import { HTMLProps, ReactNode, useState } from "react";
import { FormSection } from "Backend";
import { cn } from "components/lib/utils";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import theme from "theme";
import { LightTooltip } from "theme/Tooltips";
import { CaseProgressBySectionStatNode, ReviewerCaseProgressBySectionStatNode } from "generated/graphql";

type Bars = "complete" | "review" | "qa" | "other";
const MIN_BAR_WIDTH_PCT = 5;

function computeWidthsFromCounts(counts: number[]): number[] {
  const largestCount = counts.reduce((largest, c) => Math.max(largest, c), 1);
  // Counts below this will use min bar width
  const threshold = (largestCount * MIN_BAR_WIDTH_PCT) / 100;

  // Count how many values are below the min, so we can scale properly
  const minCount = counts.filter((c) => 0 < c && c < threshold).length;
  const spaceForRegularBars = 1 - (minCount * MIN_BAR_WIDTH_PCT) / 100;

  const normalizedCounts = counts.map((c) => {
    if (0 < c && c < threshold) return MIN_BAR_WIDTH_PCT;
    return spaceForRegularBars * (c / largestCount) * 100;
  });

  const total = counts.reduce((total, subtotal) => total + subtotal, 0);
  return [total, ...normalizedCounts];
}

export function SBRProgressChart({
  series,
  showLegend,
  height = 80,
}: {
  series: Highcharts.SeriesOptionsType[];
  showLegend: boolean;
  height?: number;
}) {
  const options: Highcharts.Options = {
    chart: {
      height,
      type: "bar",
      plotBackgroundColor: undefined,
      plotBorderWidth: 0,
      plotShadow: false,
      backgroundColor: "transparent",
    },
    title: {
      text: "",
      style: {
        fontFamily: "Inter,arial,sans-serif",
        fontWeight: "bold",
        color: theme.palette.grey[900],
      },
      align: "center",
      verticalAlign: "middle",
      y: 40,
    },
    plotOptions: {
      series: {
        stacking: "normal",
        dataLabels: {
          enabled: false,
        },
      },
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.y}</b>",
    },
    credits: {
      enabled: false,
    },
    series,
    xAxis: {
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickLength: 0,
      minorGridLineWidth: 0,
      minorTickLength: 0,
    },
    yAxis: {
      visible: false,
      endOnTick: false,
    },
    legend: {
      align: "left",
      labelFormatter: function () {
        return `${this.name} (${(this as unknown as { yData: number[] }).yData[0]})`; // Don't look at me
      },
      enabled: showLegend,
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

type CaseCountBarProps = {
  title: string;
  count: number;
  fixedWidth: number;
  inactive: boolean;
  forceShowTooltip: boolean;
};

function CaseCountBar({
  className,
  title,
  count,
  inactive,
  fixedWidth,
  forceShowTooltip,
  ...rest
}: CaseCountBarProps & HTMLProps<HTMLDivElement>) {
  const [hovering, setHovering] = useState(false);
  if (count === 0) return;
  return (
    <LightTooltip title={title} open={hovering || forceShowTooltip} placement="top">
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={cn(
          "h-full first:rounded-l last:rounded-r",
          inactive && "opacity-20",
          !inactive && "group-hover:opacity-20 group-hover:hover:opacity-100",
          className
        )}
        {...rest}
        style={{ width: `${fixedWidth}%` }}
      />
    </LightTooltip>
  );
}

function TitleTooltip({
  name,
  primary,
  primaryLabel,
  secondary,
  secondaryLabel,
}: {
  name: string;
  primary: number;
  primaryLabel: string;
  secondary: number;
  secondaryLabel: string;
}) {
  return (
    <LightTooltip
      placement="right-end"
      title={
        <span className="inline-flex flex-col">
          <span>
            {primaryLabel}: {primary.toLocaleString()}
          </span>
          <span>
            {secondaryLabel}: {secondary.toLocaleString()}
          </span>
        </span>
      }>
      {/* justify-self-start prevents stretching so tooltip shows up in right place */}
      <span className="justify-self-start text-xxs">
        <span className="text-xs font-semibold">{name}</span> ({primary.toLocaleString()})
      </span>
    </LightTooltip>
  );
}

function SBRProgressChart2({
  complete,
  qa,
  review,
  other,
  name,
  inactive,
  hovering,
}: {
  complete: number;
  qa: number;
  review: number;
  other: number;
  name: ReactNode;
  inactive?: boolean;
  hovering?: Bars;
}) {
  const [total, completeWidth, qaWidth, reviewWidth, otherWidth] = computeWidthsFromCounts([
    complete,
    qa,
    review,
    other,
  ]);
  const hoveringAnything = hovering !== undefined;
  return (total ?? 0) > 0 ? (
    <>
      {name}
      <div className={cn("flex h-3 items-center group first:rounded-l")}>
        <CaseCountBar
          count={complete}
          fixedWidth={completeWidth ?? 0}
          inactive={!!inactive || (hoveringAnything && hovering !== "complete")}
          forceShowTooltip={hovering === "complete"}
          title={`Complete - ${complete.toLocaleString()}`}
          className="bg-secondary-300"
        />
        <CaseCountBar
          count={other}
          fixedWidth={otherWidth ?? 0}
          inactive={!!inactive || (hoveringAnything && hovering !== "other")}
          forceShowTooltip={hovering === "other"}
          title={`Pending QA or Review - ${other.toLocaleString()}`}
          className="bg-[repeating-linear-gradient(45deg,#7BEBD6,#7BEBD6_10px,#34DAB8_10px,#34DAB8_20px)]"
        />
        <CaseCountBar
          count={qa}
          fixedWidth={qaWidth ?? 0}
          inactive={!!inactive || (hoveringAnything && hovering !== "qa")}
          forceShowTooltip={hovering === "qa"}
          title={`Pending QA - ${qa.toLocaleString()}`}
          className="bg-peri-300"
        />
        <CaseCountBar
          count={review}
          fixedWidth={reviewWidth ?? 0}
          inactive={!!inactive || (hoveringAnything && hovering !== "review")}
          forceShowTooltip={hovering === "review"}
          title={`Pending Review - ${review.toLocaleString()}`}
          className="bg-gray-100"
        />
      </div>
    </>
  ) : null;
}

export function SBRProgress({
  formSections,
  sectionStats,
  activeSectionId,
  summaryStats,
  activeCount,
  inactiveCount,
}: {
  lensId: number;
  formSections: FormSection[];
  sectionStats: CaseProgressBySectionStatNode[] | ReviewerCaseProgressBySectionStatNode[];
  activeSectionId?: number;
  summaryStats?: Pick<CaseProgressBySectionStatNode, "reviewCount" | "validationCount" | "completeCount">;
  activeCount?: number;
  inactiveCount?: number;
}) {
  const [hovering, setHovering] = useState<Bars>();
  const hasAnyOther = sectionStats.reduce(
    (count, stat) => count + ((stat as ReviewerCaseProgressBySectionStatNode).otherCount ?? 0),
    0
  );
  return (
    <div className="grid grid-cols-[60%_40%]">
      <div className="flex flex-col gap-4">
        {summaryStats !== undefined && activeCount !== undefined && inactiveCount !== undefined && (
          <div className="border-b border-gray-100 pb-4 grid gap-0.5">
            <SBRProgressChart2
              inactive={false}
              hovering={hovering}
              name={
                <TitleTooltip
                  name="All Cases"
                  primary={activeCount}
                  primaryLabel="Active"
                  secondary={inactiveCount}
                  secondaryLabel="Inactive"
                />
              }
              complete={summaryStats.completeCount}
              qa={summaryStats.validationCount}
              review={summaryStats.reviewCount}
              other={0}
            />
          </div>
        )}
        {formSections.map((fs) => {
          const stat = sectionStats.find((s) => s.sectionId === fs.id);
          const sectionCount = (stat?.completeCount ?? 0) + (stat?.reviewCount ?? 0) + (stat?.validationCount ?? 0);
          return sectionCount > 0 ? (
            <div key={fs.id} className="grid gap-0.5">
              <SBRProgressChart2
                inactive={activeSectionId !== undefined ? fs.id !== activeSectionId : false}
                hovering={hovering}
                name={
                  <TitleTooltip
                    name={fs.name}
                    primary={sectionCount}
                    primaryLabel="Applicable"
                    secondary={stat?.notApplicableCount ?? 0}
                    secondaryLabel="Not Applicable"
                  />
                }
                complete={stat?.completeCount ?? 0}
                qa={stat?.validationCount ?? 0}
                review={stat?.reviewCount ?? 0}
                other={(stat as ReviewerCaseProgressBySectionStatNode)?.otherCount ?? 0}
              />
            </div>
          ) : null;
        })}
      </div>
      <div className="flex flex-col items-center justify-center">
        <div className="flex flex-col cursor-help *:pt-1" onMouseLeave={() => setHovering(undefined)}>
          <div
            className="flex gap-2 items-center hover:underline"
            onMouseEnter={() => setHovering("complete")}
            onMouseLeave={() => setHovering(undefined)}>
            <div className="size-3 bg-secondary-300 rounded" />
            <p className="text-xs">Complete</p>
          </div>
          {hasAnyOther > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold uppercase">Assigned to Others</p>
              <div className="flex gap-2 items-center hover:underline" onMouseEnter={() => setHovering("other")}>
                <div className="size-3 bg-[repeating-linear-gradient(45deg,#7BEBD6,#7BEBD6_10px,#34DAB8_10px,#34DAB8_20px)] rounded" />
                <p className="text-xs">Pending QA or Review</p>
              </div>
            </div>
          )}
          {hasAnyOther > 0 && <p className="text-sm font-semibold uppercase">Assigned to Me</p>}
          <div className="flex gap-2 items-center hover:underline" onMouseEnter={() => setHovering("qa")}>
            <div className="size-3 bg-peri-300 rounded" />
            <p className="text-xs">Pending QA</p>
          </div>
          <div className="flex gap-2 items-center hover:underline" onMouseEnter={() => setHovering("review")}>
            <div className="size-3 bg-gray-100 rounded" />
            <p className="text-xs">Pending Review</p>
          </div>
        </div>
      </div>
    </div>
  );
}
